function renderFormStaticOptionsTemplate({
  fieldName,
  fieldPascal,
  singular,
  schemaName,
  staticOptions
}: {
  fieldName: string
  fieldPascal: string
  singular: string
  schemaName: string
  staticOptions: string
}): string {
  return `  const [${fieldName}Open, set${fieldPascal}Open] = React.useState(false)
  const [${fieldName}Search, set${fieldPascal}Search] = React.useState('')
  const [create${fieldPascal}OptionPending, startCreate${fieldPascal}OptionTransition] = React.useTransition()
  const { data: ${fieldName}CreatedOptions = [] as ${singular}SelectOption[] } = useQuery<${singular}SelectOption[]>({
    queryKey: ['${schemaName}', 'select-options', '${fieldName}'],
    queryFn: () => get${singular}SelectOptions('${fieldName}')
  })
  const ${fieldName}StaticOptions: Array<{ label: string; value: string }> = [${staticOptions}]
  const ${fieldName}Options = [
    ...${fieldName}StaticOptions,
    ...${fieldName}CreatedOptions.filter(
      (createdOption: ${singular}SelectOption) =>
        !${fieldName}StaticOptions.some((option) => option.value === createdOption.value)
    )
  ]`
}

function renderFormFieldArrayTemplate({
  fieldName,
  pascalFieldName
}: {
  fieldName: string
  pascalFieldName: string
}): string {
  return `  const [${fieldName}Expanded, set${pascalFieldName}Expanded] = React.useState('')
  const ${fieldName}FieldArray = useFieldArray({
    control: form.control,
    name: '${fieldName}'
  })`
}

function renderFormCurrentValueTemplate({
  fieldName,
  defaultValueFromHandlerContent
}: {
  fieldName: string
  defaultValueFromHandlerContent: string
}): string {
  return `const currentValue = form.getValues('${fieldName}')
      const selectedValues = Array.isArray(currentValue) ? currentValue : []
      const nextValues = [...new Set([...selectedValues, result.option.value])]
      ${defaultValueFromHandlerContent}form.setValue('${fieldName}', nextValues, {
        shouldDirty: true,
        shouldValidate: true
      })`
}

function renderFormContentFieldNameTemplate({
  defaultValueFromHandlerContent,
  fieldName
}: {
  defaultValueFromHandlerContent: string
  fieldName: string
}): string {
  return `${defaultValueFromHandlerContent}form.setValue('${fieldName}', result.option.value, {
        shouldDirty: true,
        shouldValidate: true
      })`
}

function renderFormTrimmedLabelTemplate({
  fieldPascal,
  singular,
  fieldName,
  schemaName,
  setCreatedOptionValue
}: {
  fieldPascal: string
  singular: string
  fieldName: string
  schemaName: string
  setCreatedOptionValue: string
}): string {
  return `  function handleCreate${fieldPascal}Option(label: string) {
    const trimmedLabel = label.trim()
    if (!trimmedLabel) return

    startCreate${fieldPascal}OptionTransition(async () => {
      const result = await create${singular}SelectOption({
        fieldName: '${fieldName}',
        label: trimmedLabel
      })

      if (!result.success || !result.option) {
        toast.error(result.error || 'Failed to create option')
        return
      }

      await queryClient.invalidateQueries({
        queryKey: ['${schemaName}', 'select-options', '${fieldName}']
      })
      ${setCreatedOptionValue}
      set${fieldPascal}Search('')
      set${fieldPascal}Open(false)
      toast.success('Option created')
    })
  }`
}

function renderFormTabValuesTemplate({ map }: { map: string }): string {
  return `const TAB_VALUES = [${map}] as const
const TAB_PARSER = parseAsStringLiteral(TAB_VALUES).withDefault(TAB_VALUES[0])

export type TabValue = (typeof TAB_VALUES)[number]

`
}

function renderEntityFormHook({
  hasCreatableSelect,
  hasNestedList,
  hasTabsField,
  hasVisibleSlugField,
  relHookImportsContent,
  singular,
  actionImportPath,
  tabParserConst,
  zodFields,
  hookOptionsName,
  slugValueHelper,
  hookName,
  relStateContent,
  creatableSelectStateContent,
  staticMultiSelectStateContent,
  hasDraft,
  defaultValues,
  plural,
  adminRoutePath,
  schemaName,
  singularText,
  defaultValueFromSyncFunctionsContent,
  fieldArrayHooksContent,
  creatableSelectHandlersContent,
  hookReturnObject
}: {
  hasCreatableSelect: boolean
  hasNestedList: boolean
  hasTabsField: boolean
  hasVisibleSlugField: boolean
  relHookImportsContent: string
  singular: string
  actionImportPath: string
  tabParserConst: string
  zodFields: string
  hookOptionsName: string
  slugValueHelper: string
  hookName: string
  relStateContent: string
  creatableSelectStateContent: string
  staticMultiSelectStateContent: string
  hasDraft: boolean
  defaultValues: string
  plural: string
  adminRoutePath: string
  schemaName: string
  singularText: string
  defaultValueFromSyncFunctionsContent: string
  fieldArrayHooksContent: string
  creatableSelectHandlersContent: string
  hookReturnObject: string
}): string {
  const creatableSelectUseQuery = hasCreatableSelect ? 'useQuery, ' : ''
  const nestedListUseFieldArray = hasNestedList ? ' useFieldArray,' : ''
  const tabsFieldImport = hasTabsField
    ? "\nimport { parseAsStringLiteral, useQueryState } from 'nuqs'"
    : ''
  const visibleSlugFieldImport = hasVisibleSlugField
    ? "import { slugify } from '@admin/utils/validation/slugify'\n"
    : ''
  const creatableSelectContent = hasCreatableSelect ? `\n  ${singular}SelectOption,` : ''
  const creatableSelectContentText = hasCreatableSelect
    ? `create${singular}SelectOption, get${singular}SelectOptions, `
    : ''
  const tabsFieldActiveTab = hasTabsField
    ? "\n  const [activeTab, setActiveTab] = useQueryState('tab', TAB_PARSER)"
    : ''
  const draftSubmitIntent = hasDraft
    ? "\n  const [submitIntent, setSubmitIntent] = React.useState<'draft' | 'publish' | null>(null)"
    : ''
  const draftOnSettled = hasDraft
    ? ',\n    onSettled: () => {\n      setSubmitIntent(null)\n    }'
    : ''
  const draftCanPublishPristineDraft = hasDraft
    ? '  const canPublishPristineDraft = Boolean(initialData && !initialData.published)\n  const canSubmitDraftAction = Boolean(initialData?.published || isDirty)\n  const canSubmitPublishAction = isDirty || canPublishPristineDraft\n'
    : ''
  const draftPublishedValue = hasDraft ? ', publishedValue: boolean = false' : ''
  const draftPublishedValueGuard = hasDraft
    ? "    if (publishedValue ? !canSubmitPublishAction : !canSubmitDraftAction) {\n      return\n    }\n    setSubmitIntent(publishedValue ? 'publish' : 'draft')\n"
    : ''
  const draftPublished = hasDraft ? ',\n        published: publishedValue' : ''
  const draftSetSubmitIntent = hasDraft ? '    setSubmitIntent(null)\n' : ''

  return `'use client'

import * as React from 'react'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { useMutation, ${creatableSelectUseQuery}useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import {${nestedListUseFieldArray} useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v3'${tabsFieldImport}
${visibleSlugFieldImport}${relHookImportsContent}
import type {
  ${singular},
  ${singular}CreateInput,${creatableSelectContent}
  ${singular}UpdateInput
} from '@admin/actions/${actionImportPath}'
import { create${singular}, ${creatableSelectContentText}update${singular} } from '@admin/actions/${actionImportPath}'

${tabParserConst}const formSchema = z.object({
${zodFields}
})

export type FormValues = z.infer<typeof formSchema>

interface ${hookOptionsName} {
  initialData?: ${singular}
}
${slugValueHelper}

export function ${hookName}({ initialData }: ${hookOptionsName}) {
  const router = useRouter()
  const queryClient = useQueryClient()${tabsFieldActiveTab}${relStateContent}${creatableSelectStateContent}${staticMultiSelectStateContent}${draftSubmitIntent}

  const defaultValues: FormValues = {
${defaultValues}
  }

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  })
  const defaultValuesRef = React.useRef(defaultValues)
  defaultValuesRef.current = defaultValues
  React.useEffect(
    () => () => {
      form.reset(defaultValuesRef.current)
    },
    [form]
  )

  const createMutation = useMutation({
    mutationFn: (data: ${singular}CreateInput) => create${singular}(data),
    onSuccess: async () => {
      toast.success('${singular} created successfully')
      await queryClient.invalidateQueries({ queryKey: ['${plural}'] })
      form.reset(defaultValues)
      router.push('${adminRoutePath}/${schemaName}')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create ${singularText}')
    }${draftOnSettled}
  })

  const updateMutation = useMutation({
    mutationFn: (data: ${singular}UpdateInput) => update${singular}(data),
    onSuccess: async () => {
      toast.success('${singular} updated successfully')
      await queryClient.invalidateQueries({ queryKey: ['${plural}'] })
      router.refresh()
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update ${singularText}')
    }${draftOnSettled}
  })

  const isPending = createMutation.isPending || updateMutation.isPending
  const { isDirty } = form.formState
${draftCanPublishPristineDraft}
${defaultValueFromSyncFunctionsContent}
${fieldArrayHooksContent}
${creatableSelectHandlersContent}
  function onSubmit(values: FormValues${draftPublishedValue}) {
${draftPublishedValueGuard}    if (initialData) {
      updateMutation.mutate({
        id: initialData.id,
        ...values${draftPublished}
      })
    } else {
      createMutation.mutate({
        ...values${draftPublished}
      } as ${singular}CreateInput)
    }
  }

  function handleErrors(errors: Record<string, unknown>) {
${draftSetSubmitIntent}    const firstError = Object.values(errors)[0] as { message?: string } | undefined
    if (firstError?.message) {
      toast.error(String(firstError.message))
    } else {
      toast.error('Please fix the form errors before submitting')
    }
  }

  return {
${hookReturnObject}
  }
}
`
}

export function renderEntityFormHookRenderer(
  request:
    | {
        kind: 'renderFormStaticOptionsTemplate'
        props: {
          fieldName: string
          fieldPascal: string
          singular: string
          schemaName: string
          staticOptions: string
        }
      }
    | {
        kind: 'renderFormFieldArrayTemplate'
        props: {
          fieldName: string
          pascalFieldName: string
        }
      }
    | {
        kind: 'renderFormCurrentValueTemplate'
        props: {
          fieldName: string
          defaultValueFromHandlerContent: string
        }
      }
    | {
        kind: 'renderFormContentFieldNameTemplate'
        props: {
          defaultValueFromHandlerContent: string
          fieldName: string
        }
      }
    | {
        kind: 'renderFormTrimmedLabelTemplate'
        props: {
          fieldPascal: string
          singular: string
          fieldName: string
          schemaName: string
          setCreatedOptionValue: string
        }
      }
    | { kind: 'renderFormTabValuesTemplate'; props: { map: string } }
    | {
        kind: 'renderEntityFormHook'
        props: {
          hasCreatableSelect: boolean
          hasNestedList: boolean
          hasTabsField: boolean
          hasVisibleSlugField: boolean
          relHookImportsContent: string
          singular: string
          actionImportPath: string
          tabParserConst: string
          zodFields: string
          hookOptionsName: string
          slugValueHelper: string
          hookName: string
          relStateContent: string
          creatableSelectStateContent: string
          staticMultiSelectStateContent: string
          hasDraft: boolean
          defaultValues: string
          plural: string
          adminRoutePath: string
          schemaName: string
          singularText: string
          defaultValueFromSyncFunctionsContent: string
          fieldArrayHooksContent: string
          creatableSelectHandlersContent: string
          hookReturnObject: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderFormStaticOptionsTemplate':
      return renderFormStaticOptionsTemplate(request.props)
    case 'renderFormFieldArrayTemplate':
      return renderFormFieldArrayTemplate(request.props)
    case 'renderFormCurrentValueTemplate':
      return renderFormCurrentValueTemplate(request.props)
    case 'renderFormContentFieldNameTemplate':
      return renderFormContentFieldNameTemplate(request.props)
    case 'renderFormTrimmedLabelTemplate':
      return renderFormTrimmedLabelTemplate(request.props)
    case 'renderFormTabValuesTemplate':
      return renderFormTabValuesTemplate(request.props)
    case 'renderEntityFormHook':
      return renderEntityFormHook(request.props)
  }

  throw new Error('Unsupported renderer request')
}
